import {
  IcoAcademicCap,
  IcoBriefcase,
  IcoCalendar,
  IcoChat,
  IcoClipboard,
  IcoDollar,
  IcoLightBulb,
  IcoUserGroup,
} from '@components/icons';
import { useDocumentTitle } from 'client/utils/use-document-title';
import { useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { ProgressBar } from '@components/progress-bar';
import { showError } from '@components/app-error';
import { StepsHeader } from '../page-builder/steps-header';
import { CreatingPageAnimation } from './creating-page-animation';
import { saveAiInterview } from './local-data';
import { useDidUpdateEffect } from 'client/utils/use-did-update-effect';
import type { AiMessage, AiProfile, AiProfileSection } from 'server/types/cal-overrides';
import { LiveKitConversation, LivekitConversationData } from '../components/livekit-conversation';
import { useRouter } from '@components/router';

const SECTIONS = [
  {
    key: 'niche',
    title: 'Coaching Niche',
    placeholder: 'e.g., Career Transitions, Executive Leadership...',
    icon: <IcoBriefcase class="size-6" />,
  },
  {
    key: 'audience',
    title: 'Target Audience',
    placeholder: 'e.g., Mid-level Managers, Creative Entrepreneurs..',
    icon: <IcoUserGroup class="size-6" />,
  },
  {
    key: 'tone',
    title: 'Coaching Tone',
    placeholder: 'e.g., Supportive, Direct, Inspirational...',
    icon: <IcoChat class="size-6" />,
  },
  {
    key: 'services',
    title: 'Offered Services',
    placeholder: 'e.g., 1:1 Coaching, Group Programs, Online Courses...',
    icon: <IcoClipboard class="size-6" />,
  },
  {
    key: 'philosophy',
    title: 'Coaching Philosophy',
    placeholder: 'e.g., Everyone has the answers within, Growth through challenge...',
    icon: <IcoLightBulb class="size-6" />,
  },
  {
    key: 'certifications',
    title: 'Certifications & Training',
    placeholder: 'e.g., ICF Certified, NLP Practitioner...',
    icon: <IcoAcademicCap class="size-6" />,
  },
  {
    key: 'pricing',
    title: 'Pricing Structure',
    placeholder: 'e.g., $150/session, $2,000 package...',
    icon: <IcoDollar class="size-6" />,
  },
  {
    key: 'session_format',
    title: 'Session Format',
    placeholder: 'e.g., Weekly Zoom Calls, Email Support, Slack Channel...',
    icon: <IcoCalendar class="size-6" />,
  },
];

function Summary({ profile }: { profile?: AiProfile }) {
  const answeredCount = Object.keys(profile?.sections || {}).length;
  const totalQuestions = SECTIONS.length;

  return (
    <div class="hidden md:flex flex-col h-full w-1/3 min-w-1/3 p-2 md:p-4 md:pt-6 bg-gray-50 overflow-auto">
      <header class="pb-4 border-gray-300">
        <div class="flex justify-between items-center font-display font-medium text-gray-900 dark:text-gray-200 text-xs">
          Interview Progress
          <span class="text-xs">
            {answeredCount} of {totalQuestions}
          </span>
        </div>
        <ProgressBar
          progress={(answeredCount / totalQuestions) * 100}
          showPercent={false}
          bgClass="bg-gray-200"
          barClass="bg-green-400"
        />
      </header>

      <div class="flex flex-col gap-2 mt-4">
        <div class="grid grid-cols-1 gap-1 lg:gap-2">
          {SECTIONS.map((section, index) => {
            const answer = profile?.sections[section.key as AiProfileSection];
            const hasAnswer = !!answer;
            const isCurrent = !hasAnswer && index === answeredCount;

            return (
              <div
                key={section.key}
                class={`p-2 relative rounded-lg shadow-sm hover:shadow-md transition-shadow flex gap-2 items-center border ${
                  hasAnswer
                    ? 'border-green-200 bg-green-100'
                    : isCurrent
                      ? 'bg-indigo-100 border-indigo-200'
                      : 'opacity-60 bg-gray-100 border-gray-100'
                }`}
                data-tooltip={answer || section.placeholder}
                data-tooltip-x="left"
              >
                <div
                  class={`${hasAnswer ? 'text-green-500 bg-green-50' : isCurrent ? 'text-indigo-500 bg-indigo-50' : 'text-gray-400 bg-gray-100'} flex-shrink-0 p-1 rounded-full`}
                >
                  {section.icon}
                </div>
                <div class="flex-grow overflow-hidden">
                  <h3 class="text-gray-900 font-medium text-xs">{section.title}</h3>
                  <p
                    class={`text-sm ${answer ? 'text-gray-800' : 'text-gray-500 italic'} ${isCurrent ? '' : 'line-clamp-1 xl:line-clamp-2'}`}
                  >
                    {answer || section.placeholder}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function InterviewConversation(props: {
  firstName: string;
  initialMessages: AiMessage[];
  initialProfile?: AiProfile;
}) {
  const router = useRouter();
  const [messages, setMessages] = useState<AiMessage[]>(props.initialMessages);
  const [isCreating, setIsCreating] = useState(false);
  const [profile, setProfile] = useState(props.initialProfile);

  async function createLandingPage() {
    setIsCreating(true);
    try {
      const initialState = await rpx.aiInterview.generateCalendarPage({
        messages,
      });
      saveAiInterview({
        messages,
        name: props.firstName,
        profile,
        aiData: initialState,
      });
      router.goto(`/interview/preview`);
    } catch (err) {
      showError(err);
      setIsCreating(false);
    }
  }

  function handleData(data: LivekitConversationData) {
    if (data.type === 'onProgress') {
      setProfile((prevProfile) => ({
        ...prevProfile,
        sections: {
          ...prevProfile?.sections,
          [data.section!]: data.value,
        },
      }));
    } else if (data.type === 'onComplete') {
      createLandingPage();
    }
  }

  useDocumentTitle(['Coaching interview']);

  useDidUpdateEffect(() => {
    saveAiInterview({
      messages,
      name: props.firstName,
      profile,
      aiData: undefined,
    });
  }, [messages, profile]);

  return (
    <>
      <div class="flex h-screen overflow-hidden">
        <div class="flex flex-col grow">
          <div class="hidden md:block">
            <StepsHeader currentStep="interview" noBorder />
          </div>
          <div class="flex grow overflow-hidden">
            <LiveKitConversation
              firstName={props.firstName}
              initialMessages={props.initialMessages}
              onMessagesChange={(updatedMessages) => setMessages(updatedMessages)}
              onData={handleData}
            />
          </div>
        </div>
        <Summary profile={profile} />
      </div>
      {isCreating && <CreatingPageAnimation />}
    </>
  );
}
